import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { Router } from '@angular/router';
import { ButtonAddComponent } from '@component/button-add/button-add.component';
import { ButtonCancelDialogComponent } from '@component/button-cancel-dialog/button-cancel-dialog.component';
import { ButtonCloseDialogComponent } from '@component/button-close-dialog/button-close-dialog.component';
import { ButtonDeleteComponent } from '@component/button-delete/button-delete.component';
import { ButtonSaveComponent } from '@component/button-save/button-save.component';
import { AccionesDialog } from '@models/base/identificadores.model';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { AccesoRapidoService } from '@services/accesosRapido/accesoRapido.service';
import { CreateAccesoRapidoRequest } from '@models/accesoRapido/createAccesoRapidoRequest.model';
import { AccesoRapidoDto } from '@models/accesoRapido/accesoRapidoDto.model';
import { UpdateAccesoRapidosRequest } from '@models/accesoRapido/updateAccesoRapidoRequest.model';

@Component({
  selector: 'app-dialog-form-accesos-rapido',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatTabsModule,
    MatDividerModule,
    MatListModule,
    NgxSpinnerModule,
    ButtonAddComponent,
    ButtonDeleteComponent,
    ButtonSaveComponent,
    ButtonCancelDialogComponent,
    ButtonCloseDialogComponent
  ],
  templateUrl: './dialog-form-accesos-rapido.component.html',
  styleUrl: './dialog-form-accesos-rapido.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFormAccesosRapidoComponent {

  // Properties
  acceso: any;
  accionesDialog = AccionesDialog;
  accesoRapido: AccesoRapidoDto | null = null;
  urlActual: string = '';
  guardando = false;
  usuarioId = 1; // TODO: Obtener del contexto de usuario

  // Opciones para los selectores
  gruposDisponibles = [
    'Trabajo',
    'Personal',
    'Desarrollo',
    'Redes Sociales',
    'Herramientas',
    'Entretenimiento',
    'Educación',
    'Noticias',
    'Finanzas',
    'Legal',
    'Editorial',
    'Salud'
  ];

  iconosDisponibles = [
    'bookmark', 'work', 'person', 'code', 'share', 'build',
    'play_arrow', 'language', 'business', 'school', 'article',
    'account_balance', 'local_hospital', 'shopping_cart',
    'restaurant', 'fitness_center', 'music_note', 'camera_alt',
    'map', 'flight', 'home', 'star', 'favorite'
  ];

  coloresDisponibles = [
    { nombre: 'Azul Principal', valor: '#1976d2' },
    { nombre: 'Verde', valor: '#4caf50' },
    { nombre: 'Naranja', valor: '#ff9800' },
    { nombre: 'Rosa', valor: '#e91e63' },
    { nombre: 'Púrpura', valor: '#9c27b0' },
    { nombre: 'Rojo', valor: '#f44336' },
    { nombre: 'Teal', valor: '#009688' },
    { nombre: 'Indigo', valor: '#3f51b5' },
    { nombre: 'Amber', valor: '#ffc107' },
    { nombre: 'Deep Orange', valor: '#ff5722' },
    { nombre: 'Brown', valor: '#795548' },
    { nombre: 'Blue Grey', valor: '#607d8b' }
  ];

  get f() { return this.form.controls; }
  form!: FormGroup;
  localData: any;

  formBuilder: FormBuilder = new FormBuilder();

  constructor(
    public dialogRef: MatDialogRef<DialogFormAccesosRapidoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private accesoRapidoService: AccesoRapidoService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.accesoRapido = this.data?.entidad || null;
    this.data.descripcion = this.accesoRapido?.titulo || 'este acceso rápido';
    this.setForm();

    // Log para debug
    console.log('Datos del diálogo:', this.data);
    console.log('Acceso rápido:', this.accesoRapido);
  }

  setForm(): void {
    this.form = this.formBuilder.group({
      Id: [this.accesoRapido?.id || null],
      Titulo: [
        this.accesoRapido?.titulo || '',
        Validators.compose([Validators.required, Validators.maxLength(100)])
      ],
      Url: [
        this.accesoRapido?.url || '',
        Validators.compose([Validators.required, Validators.pattern(/^https?:\/\/.+/)])
      ],
      Descripcion: [
        this.accesoRapido?.descripcion || '',
        Validators.maxLength(200)
      ],
      Grupo: [this.accesoRapido?.grupo || 'Personal'],
      Icono: [this.accesoRapido?.icono || 'bookmark'],
      Color: [this.accesoRapido?.color || '#1976d2'],
      EsFavorito: [this.accesoRapido?.esFavorito || false],
      Activo: [this.accesoRapido?.activo !== false]
    });

    // Escuchar cambios para actualizar la vista previa
    this.form.valueChanges.subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  setearUrlActual(event: Event): void {
    event.preventDefault();
    const urlActual = window.location.href;
    this.form.controls['Url'].setValue(urlActual);
    this.snackBar.showInfo('URL actual aplicada', 'Info');
    this.cdr.detectChanges();
  }

  async sugerirTitulo(): Promise<void> {
    const url = this.form.get('Url')?.value;
    if (!url || !this.form.controls['Url'].valid) {
      this.snackBar.showWarn('Ingresá una URL válida primero', 'Advertencia');
      return;
    }

    try {
      this.spinner.show('spTitulo');
      const tituloSugerido = await this.accesoRapidoService.sugerirTitulo(url).toPromise();

      if (tituloSugerido) {
        // Si ya hay un título, preguntar si quiere reemplazarlo
        const tituloActual = this.form.get('Titulo')?.value;
        if (tituloActual && tituloActual.trim()) {
          const confirmar = confirm('Ya tenés un título. ¿Querés reemplazarlo con la sugerencia?');
          if (!confirmar) {
            this.spinner.hide('spTitulo');
            return;
          }
        }

        this.form.controls['Titulo'].setValue(tituloSugerido);
        this.snackBar.showSuccess('Título sugerido aplicado correctamente', 'Éxito');
      } else {
        this.snackBar.showInfo('No se pudo obtener un título sugerido para esta URL', 'Info');
      }
    } catch (error) {
      console.error('Error al sugerir título:', error);
      this.snackBar.showError('Error al obtener sugerencia de título', 'Error');
    } finally {
      this.spinner.hide('spTitulo');
      this.cdr.detectChanges();
    }
  }

  async detectarCategoria(): Promise<void> {
    const url = this.form.get('Url')?.value;
    const titulo = this.form.get('Titulo')?.value;

    if (!url) {
      this.snackBar.showWarn('Ingresá una URL primero', 'Advertencia');
      return;
    }

    try {
      this.spinner.show('spCategoria');
      const resultado = await this.accesoRapidoService.detectarCategoria(url, titulo).toPromise();

      if (resultado && resultado.categoria) {
        // Verificar si la categoría detectada está en nuestras opciones
        const categoriaEncontrada = this.gruposDisponibles.find(
          grupo => grupo.toLowerCase() === resultado.categoria.toLowerCase()
        );

        if (categoriaEncontrada) {
          this.form.controls['Grupo'].setValue(categoriaEncontrada);
          this.snackBar.showSuccess(`Categoría detectada: ${categoriaEncontrada}`, 'Éxito');
        } else {
          // Agregar la nueva categoría a las opciones disponibles
          this.gruposDisponibles.push(resultado.categoria);
          this.form.controls['Grupo'].setValue(resultado.categoria);
          this.snackBar.showSuccess(`Nueva categoría detectada: ${resultado.categoria}`, 'Éxito');
        }
      } else {
        this.snackBar.showInfo('No se pudo detectar una categoría específica', 'Info');
      }
    } catch (error) {
      console.error('Error al detectar categoría:', error);
      this.snackBar.showError('Error al detectar categoría automáticamente', 'Error');
    } finally {
      this.spinner.hide('spCategoria');
      this.cdr.detectChanges();
    }
  }

  agregar(): void {
    if (!this.form.valid) {
      this.snackBar.showWarn('Por favor, completá todos los campos requeridos', 'Formulario incompleto');
      this.markFormGroupTouched();
      return;
    }

    this.guardando = true;
    this.spinner.show('spUsuario');

    const formValues = this.form.value;
    const request: CreateAccesoRapidoRequest = {
      titulo: formValues.Titulo.trim(),
      url: formValues.Url.trim(),
      descripcion: formValues.Descripcion?.trim() || '',
      grupo: formValues.Grupo,
      icono: formValues.Icono,
      orden: 0,
      visible: true,
      esFavorito: formValues.EsFavorito,
      activo: formValues.Activo,
      cantidadVisitas: 0,
      ultimoAcceso: new Date(),
      color: formValues.Color || '#1976d2',
      tags: '',
      categoria: formValues.Grupo || 'Personal',
      shortcut: '',
      esPublico: false,
      fechaExpiracion: null,
      prioridad: 0,
      requiereAutenticacion: false,
      estadoUrl: '',
      fechaVerificacionUrl: null,
      codigoRespuestaHttp: null,
      tiempoRespuestaMs: null,
      tituloExtraido: '',
      urlFavicon: '',
      configuracionJson: ''
    };

    this.accesoRapidoService.create(request).subscribe({
      next: (response: any) => {
        const resultado = response?.dto || response;
        this.snackBar.showSuccess('Acceso rápido creado correctamente', 'Éxito');
        this.dialogRef.close({ event: AccionesDialog.Agregar, data: resultado });
      },
      error: (error: any) => {
        console.error('Error al crear acceso:', error);
        this.snackBar.showError('Error al crear el acceso rápido', 'Error');
        this.guardando = false;
        this.spinner.hide('spUsuario');
        this.cdr.detectChanges();
      },
      complete: () => {
        this.spinner.hide('spUsuario');
      }
    });
  }

  modificar(): void {
    if (!this.form.valid || !this.accesoRapido) {
      this.snackBar.showWarn('Por favor, completá todos los campos requeridos', 'Formulario incompleto');
      this.markFormGroupTouched();
      return;
    }

    this.guardando = true;
    this.spinner.show('spUsuario');

    const request: UpdateAccesoRapidosRequest = {
      id: this.accesoRapido.id,
      titulo: this.form.value.Titulo.trim(),
      url: this.form.value.Url.trim(),
      descripcion: this.form.value.Descripcion?.trim() || '',
      grupo: this.form.value.Grupo,
      icono: this.form.value.Icono,
      orden: this.accesoRapido.orden,
      esFavorito: this.form.value.EsFavorito,
      visible: this.form.value.Activo,
      cantidadVisitas: this.accesoRapido.cantidadVisitas ?? 0,
      color: this.form.value.Color ?? '#1976d2',
      esPublico: this.accesoRapido.esPublico ?? false,
      requiereAutenticacion: this.accesoRapido.requiereAutenticacion ?? false,
      observaciones: this.accesoRapido.observaciones ?? ''
    };

    this.accesoRapidoService.update(request).subscribe({
      next: (resultado: AccesoRapidoDto) => {
        this.snackBar.showSuccess('Acceso rápido actualizado correctamente', 'Éxito');
        this.dialogRef.close({ event: AccionesDialog.Modificar, data: resultado });
      },
      error: (error: unknown) => {
        console.error('Error al actualizar acceso:', error);
        this.snackBar.showError('Error al actualizar el acceso rápido', 'Error');
        this.guardando = false;
        this.spinner.hide('spUsuario');
        this.cdr.detectChanges();
      },
      complete: () => {
        this.spinner.hide('spUsuario');
      }
    });
  }

  eliminar(): void {
    if (!this.accesoRapido) return;

    this.guardando = true;
    this.spinner.show('spUsuario');

    this.accesoRapidoService.delete(this.accesoRapido.id).subscribe({
      next: () => {
        this.snackBar.showSuccess('Acceso rápido eliminado correctamente', 'Éxito');
        this.dialogRef.close({ event: AccionesDialog.Eliminar });
      },
      error: (error: unknown) => {
        console.error('Error al eliminar acceso:', error);
        this.snackBar.showError('Error al eliminar el acceso rápido', 'Error');
        this.guardando = false;
        this.spinner.hide('spUsuario');
        this.cdr.detectChanges();
      },
      complete: () => {
        this.spinner.hide('spUsuario');
      }
    });
  }

  duplicar(): void {
    if (!this.accesoRapido) return;

    this.guardando = true;
    this.spinner.show('spUsuario');

    const nuevoTitulo = `${this.accesoRapido.titulo} (Copia)`;

    this.accesoRapidoService.duplicar(this.accesoRapido.id, this.usuarioId, nuevoTitulo).subscribe({
      next: (resultado: any) => {
        this.snackBar.showSuccess('Acceso rápido duplicado correctamente', 'Éxito');
        this.dialogRef.close({ event: AccionesDialog.Agregar, data: resultado });
      },
      error: (error: unknown) => {
        console.error('Error al duplicar acceso:', error);
        this.snackBar.showError('Error al duplicar el acceso rápido', 'Error');
        this.guardando = false;
        this.spinner.hide('spUsuario');
        this.cdr.detectChanges();
      },
      complete: () => {
        this.spinner.hide('spUsuario');
      }
    });
  }

  verificarUrl(): void {
    if (!this.accesoRapido) return;

    this.spinner.show('spVerificar');

    this.accesoRapidoService.verificarUrl(this.accesoRapido.id, this.usuarioId).subscribe({
      next: (resultado: { esValida: boolean; codigoRespuesta?: number; error?: string; estadoUrl?: string }) => {
        if (resultado.esValida) {
          const mensaje = `URL verificada correctamente${resultado.codigoRespuesta ? ` (${resultado.codigoRespuesta})` : ''}`;
          this.snackBar.showSuccess(mensaje, 'Verificación');
        } else {
          const mensaje = `URL inaccesible: ${resultado.error || resultado.estadoUrl}`;
          this.snackBar.showWarn(mensaje, 'Verificación');
        }
      },
      error: (error: unknown) => {
        console.error('Error al verificar URL:', error);
        this.snackBar.showError('Error al verificar la URL', 'Error');
      },
      complete: () => {
        this.spinner.hide('spVerificar');
        this.cdr.detectChanges();
      }
    });
  }

  cancelar(): void {
    this.dialogRef.close();
  }

  guardar(): void {
    this.dialogRef.close(this.acceso);
  }

  // Helper method para marcar todos los campos como touched
  private markFormGroupTouched(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
    this.cdr.detectChanges();
  }
}
